import { useCallback, useEffect, useState, useRef, useMemo } from "react";
import { FileOutlined, FilePptOutlined, MenuOutlined, UserOutlined } from "@ant-design/icons";
import { Image, Button, Space, Col, Dropdown, Drawer, Typography, Select } from "antd";
import type { MenuProps } from "antd";
import { useRouter } from "next/router";
import { StyledHeaderMain, StyledMaxWidthRow } from "@/styles/MainLayouts";
import { signOut, useSession } from "next-auth/react";
import { IconBilling, IconDashboardChromakeyProject, IconDashboardDefaultTemplateProject, IconDashboardMainHeaderUpload, IconDashboardUploadPptProject, IconFolderMove, IconPlus, IconProfileSetting, IconUser, IconWhiteLogout, IconWorkspaceInvite } from "../common/Icons";
import projectApi from "@/redux/api/projectApi";
import styled from "styled-components";
import { IconButton } from "../common/IconButton";
import { useAppDispatch, useAppSelector } from "@/hooks/redux";
import { projectActions } from "@/redux/stores/projectStore";
import { StripeModal } from "../editor/stripe/StripeModal";
import { useTranslation } from "next-i18next";
import { useGetEnvQuery } from "@/redux/api/commonApi";
import axios from "axios";
import { useDispatch } from "react-redux";
import { useGetSubscriptionsQuery, useGetUserInfoQuery } from "@/redux/api/userApi";
import { extractPlanType } from "@/lib/export-plan-type-utils";
import { useCookies } from "react-cookie";
import { getProjectState } from "@/redux/store";
import { getDashBoardState } from "@/redux/store";
import { DASHBOARD_PROJECT_TYPE, DashboardProjectType, NEW_PROJECT_CATEGORY, NewProjectCategory, QUICK_TEMPLATES_CATEGORY } from "@/constants/dashboard/common";
import { dashboardActions } from "@/redux/stores/dashboardStore";
import dashboardApi from "@/redux/api/dashboardApi";
import { poppins } from "../common/fonts";
import InviteWorkspaceModal from "../workspaces/modal/InviteWorkspaceModal";
import { MODAL_NAME } from "../editor/stripe/constants";
import { removeSessionStorageItem } from "@/lib/session-storage.lib";
import ShareHeader from "../dashboard/share/ShareHeader";

export const CustomItem = styled.button`
  display: flex;
  width: 244px;
  height: 24px;
  align-items: center;
  padding-top: 5px;
  padding-bottom: 5px;
  font-size: 13px;
  color: #12102d;
  column-gap: 5px;
  background-color: transparent;
  border: none;
  cursor: pointer;
`;

const AdminButton = styled(Button)`
  /* border: 1px solid #bec2d2; */
  border: none;
  border-color: #ffffff;
  background-color: #ffffff;
  border-radius: 10px;
  height: 32px;
  box-shadow: none;

  >span {
    text-decoration: underline;
  }

  &:hover {
    border-color: #bec2d2 !important;
    color: #222222 !important;
  }
`;

export const ProfileIcon = styled.div<{ width?: number, height?: number, backgroundColor?: string, fontSize?: number }>`
  width: ${(props) => (props.width ? `${props.width}px` : '32px')};
  height: ${(props) => (props.height ? `${props.height}px` : '32px')};
  background-color: ${(props) => (props.backgroundColor ? props.backgroundColor : 'black')};
  font-size: ${(props) => (props.fontSize ? `${props.fontSize}px` : '16px')};
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction:column;
  border-radius: 16px;
  /* margin-right: 5px; */
  color: #fff;
  font-weight: 600;
  cursor: pointer;
  font-family: Inter;

  &.unauthorized {
    /* background-color: #f5f5f5; */
    /* background-color: #CCCCCC; */
    /* color: #CCCCCC; */
    background-color: #d8d8d8;
  }
`;

export const LogoutItem = styled.div`
  border-top: "1px solid #f1f2f4";
  display: flex;
  width: 244px;
  height: 30px;
  justify-content: flex-start;
  align-items: center;
  padding: 3px 0px;
  column-gap: 5px;
`;

export const UserInfoDiv = styled.div`
  display: flex;
  width: 244px;
  height: 50px;
  justify-content: flex-start;
  align-items: center;
  border-bottom: 1px solid #f1f2f4;
`;

export const UserButtonWrapper = styled.div`
  display: flex;
  height: 40px;
  gap: 5px;
`;

// export const CreateProjectButton = styled(Dropdown)`
export const CreateProjectButton = styled(Button)`
  background-color: #3075f6;
  box-shadow: none;
  border: none;
  width: 132px;
  height: 40px;
  border-radius: 10px;
  font-weight: 600;
  font-size: 14px;
  line-height: 20px;
  gap: 4px;
  padding: 10px 16px 10px 16px;
  color: #ffffff;

  &:not(:disabled):not(.ant-btn-disabled):hover {
    background-color: #3075f6;
    color: #ffffff;
    border: none;
  }
`;

export const InviteMemberButton = styled(Button)`
  min-width: 100px;
  font-family: ${poppins.string};
  font-size: 14px;
  font-weight: 400;
  border: 1px solid #bec2d2;
  background-color: #ffffff;
  border-radius: 10px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;

  &:hover {
    border-color: #bec2d2 !important;
    color: #222222 !important;
  }
`;

export const ProjectOptionsWrapper = styled.div`
  display: flex;
  gap: 5px;
  align-items: center;
  justify-content: flex-start;
`;

export function MainHeaderRight() {
  const accountUrl = useGetEnvQuery(
    { key: "ACCOUNT_URL" },
    {
      pollingInterval: 30 * 60 * 1000,
      refetchOnFocus: false,
      refetchOnMountOrArgChange: false,
      refetchOnReconnect: false,
      skip: false,
    }
  );

  // const { data: userInfo } = useGetUserInfoQuery();
  const { accountUserInfo, workspaceList, selectedWorkspaceIndex } = useAppSelector((store) => getDashBoardState(store) ?? false);
  const currentWorkspace = useMemo(
    () =>
      workspaceList[selectedWorkspaceIndex] || {
        workspaceId: accountUserInfo?.workspaceId, 
        isPrivate: true,
      },
    [accountUserInfo?.workspaceId, selectedWorkspaceIndex, workspaceList],
  );

  const dispatch = useAppDispatch();
  const router = useRouter();
  const session = useSession();
  const [createProject] = projectApi.useCreateProjectMutation();
  const isAdmin = session?.data?.user?.role === "admin" ? true : false;
  const { t } = useTranslation("common");
  const { data: userSubscription } = useGetSubscriptionsQuery(null);
  const [cookies, setCookie, removeCookies] = useCookies(["isSignOut","first_project_create", 'tiktok_access_token', 'tiktok_refresh_token']);
  
  // const { data: quickTemplates } = dashboardApi.useGetDashboardQuickTemplateQuery({ isMobile: false }, { 
  //   refetchOnMountOrArgChange: true 
  // });
  const [templateTrigger] = dashboardApi.endpoints.getDashboardQuickTemplate.useLazyQuery();
  const selectedProjectType = useMemo(() => t('New Project'), [t]);
  const [showModal, setShowModal] = useState<boolean>(false);
  // const [selectedProjectType, setSelectedProjectType] = useState<NewProjectCategory | 'New Project'>('New Project');

  const projectOptions = useMemo((): MenuProps['items'] => {
    return [
      {
        key: NEW_PROJECT_CATEGORY.DEFAULT,
        label: (
          <ProjectOptionsWrapper>
            <IconDashboardDefaultTemplateProject />
            {t('Default Template')}
        </ProjectOptionsWrapper>
        )
      },
      {
        key: NEW_PROJECT_CATEGORY.CHROMAKEY,
        label: (
          <ProjectOptionsWrapper>
            <IconDashboardChromakeyProject/>
            {t('Chromakey Video')}
          </ProjectOptionsWrapper>
        ),
      },
      {
        key: NEW_PROJECT_CATEGORY.PPT,
        label: (
          <ProjectOptionsWrapper>
            <IconDashboardUploadPptProject />
            {t('Upload PowerPoint')}
          </ProjectOptionsWrapper>
        ),
      }
    ]
  }, [t]);

  const handleNewProject = useCallback(async (templateId: string, templateKey: string, templateUrl: string) => {
    
    dispatch(dashboardActions.setPageLoading(true));
    // router.push('/editor/test');
    // GA_EVENT({event:'Button_signup', action:'SignUp', label:'btn_submit_a' });
    if (!cookies?.first_project_create) {

      setCookie("first_project_create", true, { path: "/" });

      if (window?.dataLayer?.push) {
        window.dataLayer.push({
          event: "first_project_create",
          template_category: "",
          template_id: "",
          language_editor: "",
          subscription: extractPlanType(userSubscription?.plan),
        });
      }
    }

    if (window?.dataLayer?.push) {
      window.dataLayer.push({
        event: "project_create",
        template_category: "",
        template_id: "",
        language_editor: "",
        subscription: extractPlanType(userSubscription?.plan),
      });
    }

    if(templateKey === QUICK_TEMPLATES_CATEGORY.CHROMAKEY_EDITOR) {
      router.push(templateUrl).then(() => {
        dispatch(dashboardActions.setPageLoading(false));
      })
    } else {
      const res = await createProject({ templateId, workspaceId: currentWorkspace?.workspaceId }).unwrap();
  
      if (!res.success) {
  
        if (window?.dataLayer?.push) {
          window.dataLayer.push({
            event: "exception",
            error_type: "new_project",
            error_code: res.error.code ?? 'unknown',
            user_id: session?.data?.user?.id || "guest"
          });
        }
  
        alert(res.error?.msg);
        dispatch(dashboardActions.setPageLoading(false));
        return;
      }
      const { projectId } = res.data;
      router.push(`/editor/${projectId}`).then(() => {
        dispatch(dashboardActions.setProjectListQuery({ page: 0 }));
        dispatch(dashboardActions.setPageLoading(false))
      });
    }

  }, [cookies?.first_project_create, createProject, currentWorkspace?.workspaceId, dispatch, router, session?.data?.user?.id, setCookie, userSubscription?.plan]);


  const handleTemplateProject = useCallback((key: DashboardProjectType) => {
    let templateId: string = '';
    let templateKey: string = '';
    let templateUrl: string = '';
    templateTrigger({
      isMobile: false
    }).then((result) => {
      if(result?.data) {
        const quickTemplates = result.data;
        const findTemplate = quickTemplates.find(val => val.key === key);
        if(findTemplate) {
          templateId = findTemplate?.parentId || '';
          templateKey = findTemplate?.key || QUICK_TEMPLATES_CATEGORY.DEFAULT_TEMPLATE;
          templateUrl = findTemplate?.url || '';
        }
        handleNewProject(templateId, templateKey, templateUrl);
      }
    });
  }, [handleNewProject, templateTrigger])

  const handleSignInOut = useCallback(() => {
    if (window?.dataLayer?.push) {
      window.dataLayer.push({
        event: "log_out",
      });
    }

    removeCookies('tiktok_access_token');
    removeCookies('tiktok_refresh_token');
    
    setCookie("isSignOut", true, { path: "/" });
    removeSessionStorageItem("switchPlan")
    signOut();
  }, [accountUserInfo?.plan, removeCookies, setCookie]);

  const getSession = useCallback(async () => {
    const res = await axios.post("/api/auth/preTransfer");
    const { data } = res;
    if (data.success) {
      console.log(data.data.sessionId);
      return data.data.sessionId;
    }
    return "";
  }, []);

  const onClickProfile = useCallback(async () => {
    const sessionId = await getSession();
    if (sessionId.length > 0) {
      console.log(accountUrl.data);
      router.replace(
        `${accountUrl.data}/sso?sessionId=${sessionId}&path=profile&service=aistudios`
      );
    }
  }, [accountUrl.data, getSession, router]);

  const onClickBilling = useCallback(async () => {
    const sessionId = await getSession();
    if (sessionId.length > 0) {
      router.replace(
        `${accountUrl.data}/sso?sessionId=${sessionId}&path=billing&service=aistudios`
      );
    }
  }, [accountUrl.data, getSession, router])

  const onClickSetting = useCallback(async () => {
    const sessionId = await getSession();
    if (sessionId.length > 0) {
      if (accountUserInfo?.plan?.toLocaleLowerCase()?.includes('team')
        || accountUserInfo?.plan?.toLocaleLowerCase()?.includes('pro')) {
        router.replace(
          `${accountUrl.data}/sso?sessionId=${sessionId}&path=workspaceDetail&service=aistudios&workspaceId=${currentWorkspace?.workspaceId}`
        );
      } else if (accountUserInfo?.enterprise) {
        router.replace(
          `${accountUrl.data}/sso?sessionId=${sessionId}&path=company&service=aistudios`
        );
      } else {
        router.replace(
          `${accountUrl.data}/sso?sessionId=${sessionId}&path=profile&service=aistudios`
        );
      }
    }
  }, [accountUrl.data, accountUserInfo?.enterprise, accountUserInfo?.plan, currentWorkspace?.workspaceId, getSession, router])
  // const handleMenuClick = useCallback((item: any) => {
  //   console.log({item})
  //   switch(item.key) {
  //     case 'accountInfo':
  //       console.log('go to manage account');
  //       break;
  //     case 'logout':
  //       handleSignInOut();
  //       break;
  //   }
  // }, [handleSignInOut])

  const items: MenuProps["items"] = useMemo(() => [
    {
      label: (
        <UserInfoDiv>
          <ProfileIcon
          style={{
            backgroundColor: '#ef8d12',
          }}
          >
            {session?.data?.user?.email?.charAt(0).toUpperCase() || ""}
          </ProfileIcon>
          <div style={{ marginLeft: "10px" }}>
            <div>{session?.data?.user?.name}</div>
            <div>{session?.data?.user?.email}</div>
          </div>
        </UserInfoDiv>
      ),
      key: "accountInfo",
    },
    {
      label: (
        <CustomItem onClick={onClickProfile}>
          <IconUser style={{ verticalAlign: "middle" }} />
          {t("Profile")}
        </CustomItem>
      ),
      key: "manageAccount",
    },
    {
      label: (
        <CustomItem onClick={onClickBilling}>
          <IconBilling style={{ verticalAlign: "middle" }} />
          {t("Billing")}
        </CustomItem>
      ),
      key: "billing",
    },
    {
      label: (
        <CustomItem onClick={onClickSetting}>
          <IconProfileSetting style={{ verticalAlign: 'middle' }} />
          {t('Setting')}
        </CustomItem>
      ),
      key: 'setting'
    },
    {
      label: (
        <CustomItem onClick={handleSignInOut}>
          <IconWhiteLogout style={{ verticalAlign: "middle" }} />
          {t("Log Out")}
        </CustomItem>
      ),
      key: "logout",
    },
  ], [handleSignInOut, onClickBilling, onClickProfile, onClickSetting, session?.data?.user?.email, session?.data?.user?.name, t]);

  const handleOpenLoginPopup = useCallback(() => {
    // dispatch(openLoginModal(false));
    router.push(`/auth/signin?redirect=${encodeURIComponent(router.asPath)}`)
  }, [router]);

  const accountBtn = useMemo(() => {
    if (session.status === "authenticated") {
      return (
        <Dropdown menu={{ items }} trigger={["click"]}>
          <ProfileIcon
          css={{
            backgroundColor: '#ef8d12',
          }}
          >
            {session?.data?.user.email?.charAt(0).toUpperCase() || ""}
          </ProfileIcon>
        </Dropdown>
      );
    } else {
      return (
        <ProfileIcon onClick={handleOpenLoginPopup} className="unauthorized">
          <UserOutlined />
        </ProfileIcon>
      );
    }
  }, [handleOpenLoginPopup, items, session?.data?.user.email, session.status]);

  // const handleCreateProjectButton = useCallback((e: { key: string }) => {
  //   switch(e.key) {
  //     case NEW_PROJECT_CATEGORY.DEFAULT:
  //       handleTemplateProject(DASHBOARD_PROJECT_TYPE.DEFAULT);
  //       break;
  //     case NEW_PROJECT_CATEGORY.CHROMAKEY:
  //       handleTemplateProject(DASHBOARD_PROJECT_TYPE.CHROMAKEY);
  //       break;
  //     case NEW_PROJECT_CATEGORY.PPT:
  //       dispatch(dashboardActions.startTemplates({
  //         modalType: 'ppt',
  //         modalState: true
  //       }));
  //       break;
  //     default: 
  //     break;
  //   }
  // }, [dispatch, handleTemplateProject])

  const handleCreateProjectButton = useCallback(() => {
    dispatch(dashboardActions.startTemplates({
      modalType: "newProject",
      modalState: true
    }));
  }, [dispatch])

  // const handleTest = useCallback(async () => {
  //   const param: ChargeCreditParams = {
  //     id: "june@moneybrain.ai",
  //     credit: 0.2,
  //     changeType: "generate",
  //     backendTaskId: "12341231241",
  //     exportedBy: "june@moneybrain.ai",
  //   }
  //   const { data: charge } = await chargeCredit(param);
  //   console.log("!@#!@#!@# charge ", charge);
  // }, [])

  return (
    <Space align="center" size="large">
      {showModal && <InviteWorkspaceModal setShowModal={setShowModal} showModal={showModal}/>}
      {isAdmin && (
        <AdminButton
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            dispatch(dashboardActions.setPageLoading(true));
            router.push("/admin/account-information").then(() => {
              dispatch(dashboardActions.setPageLoading(false));
            }).catch((error) => {
              console.error(`superAdmin router error `, error);
              dispatch(dashboardActions.setPageLoading(false))
            });
          }}
        >
          {t("Super Admin")}
        </AdminButton>
      )}
      {/* <Button onClick={handleTest}>Credit</Button> */}
      <UserButtonWrapper>
        {!workspaceList[selectedWorkspaceIndex]?.isPrivate && (
          <InviteMemberButton
          onClick={(event) => {
            event.stopPropagation();
            event.preventDefault();
            if(session?.status === 'unauthenticated') {
              dispatch(projectActions.setOpenedModal(MODAL_NAME.LOGIN));
            } else {
              if(
                !accountUserInfo?.enterprise && (
                  userSubscription?.plan?.toLocaleLowerCase()?.includes('none') ||
                  userSubscription?.plan?.toLocaleLowerCase()?.includes('starter') ||
                  userSubscription?.plan?.toLocaleLowerCase()?.includes('personal')
                )
              ) {
                const noticeModalPayload = {
                  currentPlan: accountUserInfo?.plan,
                  openedModal: MODAL_NAME.NOTICE,
                  modalTitle: t("LOCKED FEATURE"),
                  modalLogoUrl: "/images/modal/mdi_lock.png",
                  noticeTitle: t("You’ve discovered a locked feature"),
                  noticeExplains: [t("By upgrading your plan, you'll unlock the ability to share a workspace")],
                }
                setShowModal(false);
                dispatch(projectActions.setNoticeModalCredit(noticeModalPayload));
                return;
              }
              
              if(workspaceList[selectedWorkspaceIndex].availableSeats <= 0) {
                if(workspaceList[selectedWorkspaceIndex].userType === 'master') {
                  // setShowModal(false);
                  const noticeModalPayload = {
                    currentPlan: accountUserInfo?.plan,
                    openedModal: MODAL_NAME.NOTICE,
                    modalTitle: t("LOCKED FEATURE"),
                    modalLogoUrl: "/images/modal/mdi_lock.png",
                    noticeTitle: t("No seats available"),
                    noticeExplains: [t("We're unable to invite more members as there are no available seats left. Please upgrade your plan.")],
                  }
                  setShowModal(false);
                  dispatch(projectActions.setNoticeModalCredit(noticeModalPayload));
                } else {
                  const noticeModalPayload = {
                    currentPlan: accountUserInfo?.plan,
                    openedModal: MODAL_NAME.NOTICE,
                    modalTitle: t("LOCKED FEATURE"),
                    modalLogoUrl: "/images/modal/mdi_lock.png",
                    noticeTitle: t("No seats available"),
                    noticeExplains: [t("We're unable to invite more members as there are no available seats left. Please request the master to upgrade your plan.")]
                  }
                  setShowModal(false);
                  dispatch(projectActions.setNoticeModalCredit(noticeModalPayload));
                }
              } else {
                setShowModal(true);
              }

            }
          }}
          >
            <IconWorkspaceInvite/>
            {t('Invite')}
          </InviteMemberButton>
        )}
        {/* <CreateProjectButton
        menu={{
          items: projectOptions,
          onClick: handleCreateProjectButton,
        }}
        trigger={['click']}
        placement="bottomRight"
        >
          <Button>
            {selectedProjectType}
          </Button>
        </CreateProjectButton> */}
        <CreateProjectButton onClick={handleCreateProjectButton}>
          {selectedProjectType}
        </CreateProjectButton>
      </UserButtonWrapper>
      {accountBtn}
    </Space>
  );
}

export default function MainHeader() {
  const router = useRouter();
  const [isMobile, setIsMobile] = useState(false);
  const [openDrawer, setOpenDrawer] = useState(false);
  const session = useSession();
  // console.log("session: ", session);
  const { t } = useTranslation("common");
  const dispatch = useAppDispatch();
  const { accountUserInfo } = useAppSelector((store) => getDashBoardState(store) ?? false);
  const [cookies, setCookie, removeCookies] = useCookies(["isSignOut","first_project_create", 'tiktok_access_token', 'tiktok_refresh_token']);
  const { automationModalOpen } = useAppSelector(
    (store) => getProjectState(store) ?? false
  );

  const automationModalOpenRef = useRef<boolean>(automationModalOpen);

  useEffect(() => {
    automationModalOpenRef.current = automationModalOpen;
  }, [automationModalOpen]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener("resize", handleResize);

    handleResize();

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const handleLogoClick = useCallback(async () => {
    if (router.isReady) await router.push(`/dashboard`).then(() => dispatch(dashboardActions.setPageLoading(false)));
  }, [dispatch, router]);

  const handleLogin = useCallback(() => {

    if (window?.dataLayer?.push && automationModalOpenRef.current) {
      window.dataLayer.push({
        event: "sign_up_start",
        product_name: 'AI Studios',
      });
    }

    dispatch(projectActions.openLoginModal(true));
  }, [dispatch]);

  const handleSignInOut = useCallback(() => {
    // if (session.status === "authenticated") {


    if (window?.dataLayer?.push) {
      window.dataLayer.push({
        event: "log_out",
      });
    }

    setCookie("isSignOut", true, { path: "/" });

    removeSessionStorageItem("switchPlan")
    signOut();

    // }
  }, [accountUserInfo?.plan, setCookie]);

  if (isMobile) {
    return (<ShareHeader />);
  }
  
  return (
    <StyledHeaderMain>
      <StyledMaxWidthRow>
        <Col>
          <Space align="center">
            <Image
              src="/images/logo_studios_renewal_ver2.svg"
              preview={false}
              alt="logo"
              onClick={handleLogoClick}
            />
          </Space>
        </Col>
        <Col>
          <MainHeaderRight />
        </Col>
      </StyledMaxWidthRow>
    </StyledHeaderMain>
  );
}
